/**
 * VersionMenu — popover au-dessus de la version pour exposer 3 docs de
 * progression projet au client connecté (Chunk 9.R.1).
 *
 * UX :
 *  - Trigger = bouton cliquable affichant `v0.1.0 · {tenantName}` (parité visuelle
 *    avec le bloc statique précédent — discret, footer sidebar).
 *  - Click → popover dropup avec 3 liens vers `/internal/progress/:slug`.
 *  - Escape / click outside / click sur un lien → close.
 *  - a11y : `aria-haspopup="menu"` + `aria-expanded` + `role="menu"` + items
 *    `role="menuitem"`.
 *
 * Le composant N'AJOUTE PAS de gating auth — il est mounted dans AppShell qui
 * n'est rendu que pour les users authentifiés (`ProtectedShellLayout`). Donc
 * implicitement gated.
 *
 * Variant :
 *  - `sidebar-desktop` : footer sidebar desktop (collapsed=false uniquement —
 *    en mode collapsed la version n'est pas affichée).
 *  - `drawer-mobile` : footer drawer mobile.
 */

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { cn } from "../lib/cn";
import { VERSION_MENU_LINKS } from "../lib/projectDocs";

type Props = {
  readonly tenantName: string | null;
  readonly variant: "sidebar-desktop" | "drawer-mobile";
};

const APP_VERSION = "v0.1.0";

export function VersionMenu({ tenantName, variant }: Props) {
  const { t } = useTranslation();
  const menuId = useId();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Click outside → close
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const node = wrapperRef.current;
      if (!node) return;
      if (e.target instanceof Node && !node.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Escape → close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const triggerLabel = t("project_progress.menu.trigger_label");

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={triggerLabel}
        className={cn(
          "flex w-full items-center gap-1 rounded text-left text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 touch-manipulation",
          variant === "sidebar-desktop" ? "px-3 py-1.5" : "px-2 py-1.5",
        )}
      >
        <span translate="no">{APP_VERSION}</span>
        <span aria-hidden="true"> · </span>
        <span className="truncate">{tenantName ?? "—"}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          aria-hidden="true"
          className={cn(
            "ml-auto shrink-0 motion-safe:transition-transform motion-reduce:transition-none",
            open && "rotate-180",
          )}
        >
          <path d="M2 6l3-3 3 3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={triggerLabel}
          className="absolute bottom-full left-0 z-30 mb-1 w-full min-w-[12rem] rounded-md border border-slate-200 bg-white shadow-lg ring-1 ring-slate-200/50 motion-safe:animate-fade-in"
        >
          <ul className="py-1">
            {VERSION_MENU_LINKS.map((link) => (
              <li key={link.href}>
                <Link
                  to={link.href}
                  role="menuitem"
                  onClick={() => setOpen(false)}
                  className="block rounded px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 touch-manipulation"
                >
                  {t(link.i18nKey)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
